import { required, useListContext, useTranslate } from "ra-core";
import { useWatch } from "react-hook-form";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { CreateButton } from "@/components/admin/create-button";
import { Create } from "@/components/admin/create";
import { DataTable } from "@/components/admin/data-table";
import { DateInput } from "@/components/admin/date-input";
import { Edit } from "@/components/admin/edit";
import { List } from "@/components/admin/list";
import { MobileRecordCard } from "@/components/admin/mobile-record-card";
import { NumberInput } from "@/components/admin/number-input";
import { ReferenceField } from "@/components/admin/reference-field";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { SimpleForm } from "@/components/admin/simple-form";
import { TextInput } from "@/components/admin/text-input";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { TopToolbar } from "../../atomic-crm/layout/TopToolbar";
import { findChoice, INVOICE_STATUSES } from "../common/constants";
import {
  emptyToNull,
  isOverdue,
  StatusBadge,
  todayIso,
  useFormatDate,
  useFormatMoney,
} from "../common/components";
import { useNextNumber } from "./quotes";

const gross = (i: Record<string, any>) =>
  Number(i.amount) * (1 + Number(i.tax_rate) / 100);

/** An issued invoice past its due date counts as overdue even if nobody changed it. */
const effectiveStatus = (i: Record<string, any>) =>
  i.status === "issued" && isOverdue(i.due_date) ? "overdue" : i.status;

const Totals = () => {
  const { data } = useListContext();
  const translate = useTranslate();
  const formatMoney = useFormatMoney();
  const pending = (data ?? [])
    .filter((i) => ["issued", "overdue"].includes(i.status))
    .reduce((sum, i) => sum + gross(i), 0);
  const paid = (data ?? [])
    .filter((i) => i.status === "paid")
    .reduce((sum, i) => sum + gross(i), 0);
  return (
    <div className="mb-3 space-y-1">
      <div className="flex gap-6 text-sm">
        <p>
          <span className="text-muted-foreground">
            {translate("hub.invoices.pending_total")}:
          </span>{" "}
          <span className="font-semibold text-amber-600">
            {formatMoney(pending)}
          </span>
        </p>
        <p>
          <span className="text-muted-foreground">
            {translate("hub.invoices.paid_total")}:
          </span>{" "}
          <span className="font-semibold text-green-600">
            {formatMoney(paid)}
          </span>
        </p>
      </div>
      <p className="text-xs text-muted-foreground">
        {translate("hub.invoices.disclaimer")}
      </p>
    </div>
  );
};

const InvoiceList = () => {
  const formatDate = useFormatDate();
  const formatMoney = useFormatMoney();
  return (
    <List
      resource="invoices"
      sort={{ field: "issue_date", order: "DESC" }}
      perPage={100}
      filters={[
        <SelectInput
          key="status"
          source="status"
          choices={INVOICE_STATUSES}
          emptyText="hub.common.all"
          alwaysOn
        />,
      ]}
      actions={
        <TopToolbar>
          <CreateButton label="hub.invoices.new" />
        </TopToolbar>
      }
    >
      <Totals />
      <DataTable
        rowClick="edit"
        bulkActionButtons={false}
        mobileCard={(i) => (
          <MobileRecordCard
            title={i.number}
            trailing={
              <StatusBadge
                choice={findChoice(INVOICE_STATUSES, effectiveStatus(i))}
              />
            }
            subtitle={
              i.company_id != null ? (
                <ReferenceField
                  source="company_id"
                  reference="companies"
                  link={false}
                />
              ) : null
            }
            meta={[
              <span className="font-medium text-foreground">
                {formatMoney(gross(i))}
              </span>,
              formatDate(i.issue_date),
              i.due_date ? (
                <span
                  className={cn(
                    effectiveStatus(i) === "overdue" &&
                      "font-medium text-red-600",
                  )}
                >
                  {formatDate(i.due_date)}
                </span>
              ) : null,
            ]}
          />
        )}
      >
        <DataTable.Col source="number" />
        <DataTable.Col source="company_id">
          <ReferenceField
            source="company_id"
            reference="companies"
            link="show"
          />
        </DataTable.Col>
        <DataTable.Col source="deal_id">
          <ReferenceField source="deal_id" reference="deals" link="show" />
        </DataTable.Col>
        <DataTable.Col
          source="issue_date"
          render={(i) => formatDate(i.issue_date)}
        />
        <DataTable.Col
          source="due_date"
          render={(i) => (
            <span
              className={cn(
                effectiveStatus(i) === "overdue" && "font-medium text-red-600",
              )}
            >
              {formatDate(i.due_date)}
            </span>
          )}
        />
        <DataTable.Col
          source="amount"
          label="hub.quotes.total"
          render={(i) =>
            `${formatMoney(gross(i))} (${formatMoney(i.amount)} + IVA)`
          }
        />
        <DataTable.Col
          source="status"
          render={(i) => (
            <StatusBadge
              choice={findChoice(INVOICE_STATUSES, effectiveStatus(i))}
            />
          )}
        />
      </DataTable>
    </List>
  );
};

const MilestoneOfDeal = () => {
  const dealId = useWatch({ name: "deal_id" });
  return (
    <ReferenceInput
      source="milestone_id"
      reference="deal_milestones"
      filter={
        dealId != null && dealId !== "" ? { deal_id: dealId } : { "id@eq": -1 }
      }
    >
      <SelectInput
        optionText="title"
        emptyText="hub.common.none"
        parse={emptyToNull}
      />
    </ReferenceInput>
  );
};

const InvoiceInputs = () => (
  <div className="flex flex-col gap-4">
    <div className="grid gap-4 sm:grid-cols-3 [&>*]:min-w-0 [&>*>*]:min-w-0">
      <TextInput source="number" validate={required()} />
      <SelectInput
        source="status"
        choices={INVOICE_STATUSES}
        validate={required()}
      />
      <DateInput source="paid_at" />
    </div>
    <div className="grid gap-4 sm:grid-cols-3 [&>*]:min-w-0 [&>*>*]:min-w-0">
      <ReferenceInput source="company_id" reference="companies">
        <AutocompleteInput
          label="resources.invoices.fields.company_id"
          optionText="name"
        />
      </ReferenceInput>
      <ReferenceInput source="deal_id" reference="deals">
        <AutocompleteInput
          label="resources.invoices.fields.deal_id"
          optionText="name"
        />
      </ReferenceInput>
      <MilestoneOfDeal />
    </div>
    <div className="grid gap-4 sm:grid-cols-4 [&>*]:min-w-0 [&>*>*]:min-w-0">
      <DateInput source="issue_date" validate={required()} />
      <DateInput source="due_date" />
      <NumberInput source="amount" min={0} step={0.01} validate={required()} />
      <NumberInput source="tax_rate" min={0} max={100} />
    </div>
    <TextInput source="notes" multiline rows={2} />
  </div>
);

/** Paying an invoice stamps paid_at. */
const transform = (data: Record<string, any>) => ({
  ...data,
  paid_at:
    data.status === "paid" ? data.paid_at || todayIso() : data.paid_at || null,
});

const InvoiceCreate = () => {
  const number = useNextNumber("invoices", "F");
  const due = new Date();
  due.setDate(due.getDate() + 30);
  return (
    <Create
      resource="invoices"
      redirect="list"
      transform={transform}
      className="mx-auto w-full max-w-4xl"
    >
      <Card>
        <CardContent>
          <SimpleForm
            className="max-w-none"
            defaultValues={{
              number,
              status: "issued",
              issue_date: todayIso(),
              due_date: due.toISOString().slice(0, 10),
              tax_rate: 21,
              amount: 0,
            }}
          >
            <InvoiceInputs />
          </SimpleForm>
        </CardContent>
      </Card>
    </Create>
  );
};

const InvoiceEdit = () => (
  <Edit
    resource="invoices"
    redirect="list"
    mutationMode="pessimistic"
    transform={transform}
    className="mx-auto w-full max-w-4xl"
  >
    <Card>
      <CardContent>
        <SimpleForm className="max-w-none">
          <InvoiceInputs />
        </SimpleForm>
      </CardContent>
    </Card>
  </Edit>
);

export default {
  list: InvoiceList,
  create: InvoiceCreate,
  edit: InvoiceEdit,
  recordRepresentation: "number",
};
